// Schema options' functions must be pure, in the sense that they may not access
// data that is not passed to them; so that they may access arbitrary data, this
// data must be provided to them in the form of context
export interface Context {
  ancestors: Ancestor[];
  [contextFields: string]: any;
}

// Ancestor of the current value; `ancestor.value[ancestor.id]` should return
// the current value
export interface Ancestor {
  id: number | string;
  value: any;
  schema: Schema;
}

export const EMPTY_CONTEXT: Context = {ancestors: []};

export interface Options<T = any> {
  // A label used to identify the schema in a human-friendly manner; may be
  // dynamic and depend on its value if a function is passed to it
  label?: string | ((value: T | null, context?: Context) => string | null);
  // The initial value should be ignored if a computed value is defined
  initialValue?: T | ((context?: Context) => T | null) | null;
  computedValue?: (context?: Context) => T | null;
  // Whether the value is required (if it may be `null`); this function **must**
  // be used to determine if the value is mandatory, i.e., all form values are
  // valid when their value is `null` unless `isRequired` is `true`
  isRequired?: boolean | ((context?: Context) => boolean);
  // This function is never called with a `null` value (`null` values are always
  // valid unless `isRequired` is `true`)
  isValid?: (value: T, context?: Context) => boolean;
  // User-defined extra schema information
  extraFields?: Record<string, any>;
}

export abstract class Schema<T = any, O extends Options<T> = Options<T>> {
  protected _options: O;

  constructor(options: O) {
    this._options = options;
  }

  // Provide support for using `JSON.stringify`
  public toJSON(): object {
    return {
      label: this._options.label,
      initialValue: this.isComputed ? undefined : this._options.initialValue,
      isComputed: this.isComputed,
      isRequired: this._options.isRequired,
      isValid: this._options.isValid,
      extraFields: this._options.extraFields,
    };
  }

  public label(
    value: T | null,
    context: Context = EMPTY_CONTEXT,
  ): string | null {
    return this._options.label === undefined
      ? null
      : typeof this._options.label === 'function'
        ? this._options.label(value, context)
        : this._options.label;
  }

  public initialValue(context: Context = EMPTY_CONTEXT): T | null {
    return this._options.initialValue === undefined || this.isComputed
      ? null
      : typeof this._options.initialValue === 'function'
        ? this._options.initialValue(context)
        : this._options.initialValue;
  }

  public computedValue(context: Context = EMPTY_CONTEXT): T | null {
    return this.isComputed ? this._options.computedValue!(context) : null;
  }

  public get isComputed(): boolean {
    return this._options.computedValue !== undefined;
  }

  public isRequired(context: Context = EMPTY_CONTEXT): boolean {
    return this._options.isRequired === undefined
      ? false
      : typeof this._options.isRequired === 'function'
        ? this._options.isRequired(context)
        : this._options.isRequired;
  }

  // All schema types must implement `typeIsValid` to check whether a given
  // value has a valid type in the schema. `strict` set to `true` complains
  // about missing fields in record values
  public abstract typeIsValid(value: any, strict?: boolean): value is T | null;

  // Method that transforms complex structures into MobX `observable`
  // structures, transforming inner computed field/element values into Mobx
  // `computed`s
  public abstract valueToMobx(value: T | null, context?: Context): T | null;

  // Method used to return a JSON representation of the value that computes all
  // computed values within as necessary in a lazy manner
  public abstract valueToJS(value: T | null, context?: Context): T | null;

  // All schema types must implement `valueIsValid` and should access
  // `requiredIsValid` and `customFunctionIsValid` in their implementation, a
  // context is required so that schema options' functions have more information
  // in their disposal; an optional function may be provided to add information
  // a value before validating its children
  public abstract valueIsValid(value: T | null, context?: Context): boolean;

  public requiredIsValid(
    value: T | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return value !== null || !this.isRequired(context);
  }

  // When determining if a value is valid, this function should **always** be
  // the last one accessed, so that values passed to the custom function are
  // always _otherwise valid_
  public customFunctionIsValid(
    value: T | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return (
      value === null ||
      this._options.isValid === undefined ||
      this._options.isValid(value, context)
    );
  }

  public getExtraField(field: string): any {
    return this._options.extraFields && this._options.extraFields[field];
  }

  // Transform `undefined` values into `null`
  protected _nullifyValue(value?: T | null): T | null {
    return value === undefined ? null : value;
  }

  // Builds the context of a child value given the current value, the id used to
  // access the child value from the current value, and the current context
  protected _buildChildContext(
    value: T,
    id: number | string,
    context: Context = EMPTY_CONTEXT,
  ): Context {
    return {
      ...context,
      ancestors: [...(context.ancestors || []), {value, id, schema: this}],
    };
  }

  // Merge `source` and `destination` into a new value
  protected _merge(destination, source): any {
    // Merge arrays
    if (Array.isArray(source) && Array.isArray(destination)) {
      return source.map((elem, i) => this._merge(destination[i], elem));
    }
    // Merge objects
    if (
      source !== null &&
      destination !== null &&
      typeof source === 'object' &&
      typeof destination === 'object'
    ) {
      const obj = {};
      Object.keys(source).forEach(
        prop => (obj[prop] = this._merge(destination[prop], source[prop])),
      );
      Object.keys(destination).forEach(prop => {
        if (!(prop in obj)) {
          obj[prop] = destination[prop];
        }
      });
      return obj;
    }
    // Merge other value types
    return source !== undefined ? source : destination;
  }
}
